import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ASSETS, CAPABILITIES, IDENTITY_PROFILE } from "@/data/content";
import { fetchStatus } from "@/lib/api";
import PageHero from "@/components/arc/PageHero";
import Reveal from "@/components/arc/Reveal";
import { HudRow, Panel, SimTag, StatusBadge } from "@/components/arc/ui";
import { GlowOrbs } from "@/components/arc/Backdrops";

const Identity = () => {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    fetchStatus().then(setStatus);
  }, []);

  return (
    <>
      <PageHero
        index="04"
        eyebrow="IDENTITY"
        title="EVERY MACHINE NEEDS AN IDENTITY"
        sub="A machine profile interface: who the unit is, what it can do, and what it has proven. All readings on this page are a simulation of the identity concept."
      />
      <section className="relative overflow-hidden pb-24 md:pb-36">
        <GlowOrbs />
        <div className="relative mx-auto grid max-w-[1600px] gap-8 px-5 md:px-8 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <Panel className="scanlines overflow-hidden p-8 md:p-10" data-testid="identity-card">
              <div className="flex flex-wrap items-start justify-between gap-6">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <span className="absolute inset-0 animate-pulse-ring rounded-full border border-electric/50" />
                    <img
                      src={ASSETS.logo}
                      alt="ARCIMUS-001 unit emblem"
                      className="h-24 w-24 rounded-full object-cover ring-1 ring-ice/40"
                    />
                  </div>
                  <div>
                    <p className="hud-label">MACHINE ID</p>
                    <h2 className="mt-1 font-display text-2xl font-black tracking-[0.12em] text-chrome text-glow-blue md:text-3xl" data-testid="identity-unit">
                      {IDENTITY_PROFILE.unit}
                    </h2>
                    <p className="mt-2 font-term text-[10px] tracking-[0.3em] text-electric">
                      THE FIRST PRIME OF ARC
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <StatusBadge status="LIVE" />
                  <SimTag>SIMULATION</SimTag>
                </div>
              </div>

              <div className="mt-10 grid gap-x-10 sm:grid-cols-2">
                <div>
                  <HudRow label="STATUS" value={IDENTITY_PROFILE.status} accent />
                  <HudRow label="CLASS" value={IDENTITY_PROFILE.class} />
                  <HudRow label="NETWORK" value={status?.network || IDENTITY_PROFILE.network} />
                  <HudRow label="MODE" value={status?.mode || IDENTITY_PROFILE.mode} accent />
                </div>
                <div>
                  <HudRow label="REPUTATION" value={IDENTITY_PROFILE.reputation} />
                  <HudRow label="MISSIONS" value={IDENTITY_PROFILE.missions} />
                  <HudRow label="TRANSACTIONS" value={IDENTITY_PROFILE.transactions} />
                  <HudRow label="MEMORY" value={IDENTITY_PROFILE.memory} accent />
                </div>
              </div>

              <p className="mt-8 border-t border-ice/10 pt-5 font-term text-[10px] leading-relaxed tracking-[0.18em] text-silver/70">
                CONCEPTUAL INTERFACE. REPUTATION, MISSION, AND TRANSACTION COUNTERS READ ZERO
                BECAUSE NO LIVE MACHINE HISTORY EXISTS.
              </p>
            </Panel>
          </Reveal>

          <div className="space-y-8">
            <Reveal delay={0.1}>
              <Panel className="p-8" data-testid="capabilities-panel">
                <p className="hud-label">CAPABILITIES</p>
                <div className="mt-6 space-y-6">
                  {CAPABILITIES.map((cap, i) => (
                    <div key={cap.label}>
                      <div className="flex items-center justify-between">
                        <span className="font-term text-[11px] tracking-[0.22em] text-chrome">{cap.label}</span>
                        <span className="font-term text-[11px] tracking-[0.2em] text-electric">{cap.level}%</span>
                      </div>
                      <div className="mt-2 h-1.5 w-full bg-steel">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${cap.level}%` }}
                          viewport={{ once: true }}
                          transition={{ duration: 1.1, delay: 0.2 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                          className="h-full bg-gradient-to-r from-arc to-electric shadow-[0_0_12px_rgba(0,168,255,0.6)]"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-6 font-term text-[10px] tracking-[0.18em] text-silver/60">
                  ILLUSTRATIVE CAPABILITY MODEL — NOT A BENCHMARK.
                </p>
              </Panel>
            </Reveal>

            <Reveal delay={0.2}>
              <Panel className="p-8" data-testid="activity-panel">
                <p className="hud-label">MISSION HISTORY {"//"} ACTIVITY {"//"} WALLET</p>
                <div className="mt-6 space-y-4">
                  <HudRow label="MISSION HISTORY" value="NO RECORDS" />
                  <HudRow label="RECENT ACTIVITY" value="NO RECORDS" />
                  <HudRow label="WALLET STATUS" value="CONCEPT — NOT CONNECTED" />
                </div>
                <p className="mt-6 text-sm leading-relaxed text-silver">
                  The identity layer is designed so these fields fill with real, verifiable history
                  as the protocol phases advance. Until then, they stay honestly empty.
                </p>
              </Panel>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default Identity;
